import type React from "react";
import { Box, Typography } from "@mui/material";
import { Person } from "@mui/icons-material";
import backgroundImag from "../images/thumbnailbackground.png";
import muzukuruLogo from "../../../../assets/muzukurublack.png";

interface FuneralPreviewCardProps {
  deceasedName: string;
  birthDate: string;
  deathDate: string;
  serviceDate?: string;
  serviceTime?: string;
  timeZone: string;
  location?: string;
  isLiveStream: boolean;
  broadcastBy: string;
  broadcastByLogo?: string;
  backgroundImage?: string;
  circleImageUrl?: string;
}

const FuneralPreviewCard: React.FC<FuneralPreviewCardProps> = ({
  deceasedName,
  birthDate,
  deathDate,
  serviceDate,
  serviceTime,
  timeZone,
  location,
  isLiveStream,
  broadcastBy,
  broadcastByLogo = muzukuruLogo,
  backgroundImage = backgroundImag,
  circleImageUrl = "",
}) => {
  return (
    <Box
      sx={{
        // width: { xs: 320, sm: 360, md: 400 },
        width: "100%",
        maxWidth: { xs: 320, sm: 360, md: 400 },
        //aspectRatio: "16/9",
        zoom:0.8,
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: "text.primary",
        fontFamily: '"Montserrat", sans-serif',
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 1, sm: 1.5, md: 1 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 1, sm: 1.5, md: 2 },
          overflow: "hidden",
        }}
      >
        {/* LEFT SECTION */}
        <Box
          sx={{
            flex: "0 0 30%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 0.5, sm: 0.75, md: 1 },
            minWidth: 80,
          }}
        >
          {/* Avatar */}
          <Box
            sx={{
              width: "100%",
              aspectRatio: "1/1",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #000000",
              backgroundColor: "rgba(255,255,255,0.1)",
              mb: { xs: 0.5, md: 1 },
              overflow: "hidden",
              backgroundImage: circleImageUrl
                ? `url(${circleImageUrl})`
                : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            {!circleImageUrl && (
              <Person
                sx={{
                  fontSize: { xs: 30, sm: 40, md: 50 },
                  color: "text.primary",
                }}
              />
            )}
          </Box>

          {/* Birth/Death Dates */}
          <Typography
            sx={{
              fontSize: { xs: 6, sm: 8, md: 10 },
              opacity: 0.9,
              textAlign: "center",
              display: { xs: "none", sm: "block" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {birthDate} – {deathDate}
          </Typography>

          {/* Broadcast By */}
          <Box sx={{ textAlign: "center", mt: { xs: 0.5, md: 1 } }}>
            <Typography
              sx={{
                fontSize: { xs: 10, sm: 12, md: 10 },
                fontWeight: 400,
                mb: 0.5,
                letterSpacing: { xs: "0.5px", md: "1px" },
              }}
            >
              Broadcast by
            </Typography>
            {broadcastByLogo ? (
              <Box
                component="img"
                src={broadcastByLogo}
                alt={broadcastBy}
                sx={{
                  width: { xs: 40, sm: 60, md: 80 },
                  objectFit: "contain",
                }}
              />
            ) : (
              <Typography
                sx={{
                  fontSize: { xs: 12, sm: 12, md: 13 },
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
              >
                {broadcastBy}
              </Typography>
            )}
          </Box>
        </Box>

        {/* RIGHT SECTION */}
        <Box
          sx={{
            flex: "0 0 65%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            gap: { xs: 0.4, sm: 0.6, md: 0.8 },
            minWidth: 150,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 12, sm: 14, md: 14 },
              fontStyle: "italic",
              opacity: 0.85,
            }}
          >
            In Loving Memory Of
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 16, sm: 18, md: 22 },
              fontWeight: 700,
              lineHeight: 1.2,
              textTransform: "capitalize",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {deceasedName}
          </Typography>

          {/* Live Stream Badge */}
          {isLiveStream && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.3, sm: 0.5 },
                mt: { xs: 0.3, md: 0.5 },
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  backgroundColor: "#FF0000",
                  px: { xs: 0.5, sm: 0.75, md: 1 },
                  py: { xs: 0.5, sm: 1, md: 1 },
                  fontWeight: 700,
                  borderRadius: "0px 12px 0px 12px",
                  border: "1px solid white",
                  color: "white",
                  fontSize: { xs: 10, sm: 12, md: 12 },
                }}
              >
                LIVE
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 10, sm: 12, md: 12 },
                  fontWeight: 600,
                }}
              >
                STREAM
              </Typography>
            </Box>
          )}

          <Typography
            sx={{
              fontSize: { xs: 12, sm: 14, md: 14 },
              mt: { xs: 0.3, md: 0.5 },
              fontWeight: 400,
            }}
          >
            {serviceDate}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 12, sm: 14, md: 16 },
              fontWeight: 700,
            }}
          >
            {serviceTime} ({timeZone})
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 8, sm: 10, md: 10 },
              textTransform: "uppercase",
              letterSpacing: { xs: "0.5px", md: "1px" },
              opacity: 0.8,
              mt: { xs: 0.4, md: 0.8 },
            }}
          >
            BROADCASTING LIVE FROM
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 10, sm: 12, md: 14 },
              fontWeight: 500,
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "center",
              maxWidth: "100%",
              px: { xs: 0.5, sm: 1 },
            }}
          >
            {location}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FuneralPreviewCard;
